import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Box, SvgIcon, type SvgIconProps, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";

/**
 * Where the day sits, in the icon's own 24-unit viewBox: `CalendarTodayOutlined`'s page, under its header band,
 * spans x 4–20 and y 10–21. Drawing the number in that same viewBox keeps it centred and in proportion at every
 * icon size, with no pixel offsets to keep in step.
 */
const PAGE_CENTER_X = 12;
const PAGE_CENTER_Y = 15.5;
/** Two digits at this size fill the page's width with about a unit to spare either side. */
const DAY_FONT_SIZE = 10;
const DAY_FONT_WEIGHT = 700;

type Props = {
	/** The day of the month the page shows, e.g. 24. */
	day: number;
	/** The icon's MUI size; the number scales with it. */
	fontSize: SvgIconProps["fontSize"];
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A calendar glyph with a day of the month printed on its page, as a phone's calendar icon has — so a "today"
 * control says which day it goes to. Paints in `currentColor`, like any MUI icon.
 */
export const CalendarDayIcon: FC<Props> = ({ day, fontSize, sx, dataTestId }) => (
	<Box component="span" data-testid={dataTestId} sx={[{ position: "relative", display: "inline-flex" }, ...(Array.isArray(sx) ? sx : [sx])]}>
		<CalendarTodayOutlinedIcon fontSize={fontSize} />
		<SvgIcon fontSize={fontSize} sx={{ position: "absolute", inset: 0 }}>
			<text
				x={PAGE_CENTER_X}
				y={PAGE_CENTER_Y}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={DAY_FONT_SIZE}
				fontWeight={DAY_FONT_WEIGHT}
				style={{ fontVariantNumeric: "tabular-nums" }}
			>
				{day}
			</text>
		</SvgIcon>
	</Box>
);
